import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { keyframes } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { motion, useReducedMotion } from "framer-motion";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InfoIcon from "@mui/icons-material/Info";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SchoolIcon from "@mui/icons-material/School";

import ActionButton from "../Components/ActionButton";
import DownloadCvButton from "../Components/DownloadCvButton";
import PageMeta from "../Components/PageMeta";
import ProjectCard from "../Components/ProjectCard";
import ProofStrip from "../Components/ProofStrip";
import Reveal from "../Components/Reveal";
import SectionHeading from "../Components/SectionHeading";
import SkillCard from "../Components/SkillCard";
import SocialLinks from "../Components/SocialLinks";
import { backgroundSummary } from "../data/about";
import { aboutSection, projectsSection } from "../data/home";
import { featuredProjects } from "../data/projects";
import { assets, site } from "../data/site";
import { skillHighlights } from "../data/skills";

const AVATAR_SIZE = { xs: 200, sm: 260, md: 300 };

/** Slow pendulum, pivoting on the tag's hole. Small angles read as weight. */
const tagSwing = keyframes({
  "0%, 100%": { transform: "translate(-50%, -9%) rotate(-4deg)" },
  "50%": { transform: "translate(-50%, -9%) rotate(2.5deg)" },
});

const CARD_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
  gap: 2.5,
  width: "100%",
};

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const reduceMotion = useReducedMotion();

  const fadeUp = (delay) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <Box
      component="section"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        pt: { xs: 4, md: 6 },
        pb: { xs: 6, md: 8 },
      }}
    >
      <motion.div
        initial={reduceMotion ? false : { scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Avatar
            src={assets.heroAvatar}
            alt={site.name}
            sx={(theme) => ({
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              // The portrait is shot on a near-white backdrop, so a white ring
              // leaves it edgeless on a white page and the tag appears to hang
              // off nothing. The hairline token resolves for both modes.
              border: theme.tokens.hairline,
              boxShadow: theme.tokens.cardShadow,
            })}
          />

          {/*
           * The tag hangs off the portrait like a real luggage label: its hole
           * is pinned to the ring at ~35° below horizontal, it pivots from
           * that hole, and it swings very slightly. Anchoring the hole to the
           * ring is what stops it reading as a sticker pasted on top; 35°
           * rather than 45° keeps it clear of the heading underneath.
           */}
          <Box
            component="img"
            src={assets.openToWork}
            alt="Open to work"
            sx={{
              position: "absolute",
              zIndex: 1,
              left: "90%",
              top: "77%",
              width: { xs: 66, sm: 78, md: 90 },
              transform: "translate(-50%, -9%) rotate(-4deg)",
              transformOrigin: "50% 9%",
              animation: `${tagSwing} 5s ease-in-out infinite`,
              filter: "drop-shadow(0 8px 12px rgba(0, 0, 0, 0.28))",
              pointerEvents: "none",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        </Box>
      </motion.div>

      {/*
        The greeting keeps his voice but gives up the headline slot. A visitor
        who reads only the largest line on the page should come away knowing
        what he builds, and "Hi, I'm Chafic" does not carry that.
      */}
      <motion.div {...fadeUp(0.12)}>
        <Typography
          component="p"
          variant="subtitle1"
          color="text.secondary"
          sx={{ mt: 4, mb: 1 }}
        >
          Hi, I&rsquo;m Chafic
        </Typography>
      </motion.div>

      <motion.div {...fadeUp(0.18)}>
        <Typography
          variant="display"
          sx={{ mb: 2, maxWidth: "18ch", mx: "auto", textWrap: "balance" }}
        >
          {site.headline}
        </Typography>
      </motion.div>

      <motion.div {...fadeUp(0.26)}>
        <Typography
          component="p"
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: "52ch", mx: "auto", mb: 4, textWrap: "pretty" }}
        >
          {site.tagline}
        </Typography>
      </motion.div>

      {/*
        Numbers earn the claim above them. They come from data/experience.js
        via site.proofPoints, so the hero cannot quote a figure the work
        history does not support.
      */}
      <motion.div {...fadeUp(0.34)} style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <ProofStrip />
      </motion.div>

      <Divider sx={{ width: 64, my: 4, borderColor: "divider" }} />

      <motion.div {...fadeUp(0.42)}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <ActionButton
            component={RouterLink}
            to="/contact"
            size={isMobile ? "medium" : "large"}
            startIcon={<MailOutlineIcon />}
          >
            Get in Touch
          </ActionButton>
          <ActionButton
            component={RouterLink}
            to="/projects"
            tone="subtle"
            size={isMobile ? "medium" : "large"}
            startIcon={<FolderOpenIcon />}
          >
            View Projects
          </ActionButton>
          <DownloadCvButton size={isMobile ? "medium" : "large"} />
        </Box>
      </motion.div>

      <motion.div {...fadeUp(0.5)}>
        <SocialLinks sx={{ mt: 3 }} />
      </motion.div>
    </Box>
  );
};

const HomePage = () => (
  <Container maxWidth="xl">
    <PageMeta description={site.description} />

    <Hero />

    <Divider sx={{ width: "50%", mx: "auto" }} />

    {/* About preview */}
    <Box
      component="section"
      aria-labelledby="about-preview"
      sx={(theme) => ({
        py: { xs: 5, md: 8 },
        px: { xs: 2, md: 4 },
        my: 5,
        borderRadius: 3,
        backgroundImage: theme.tokens.sectionGradient,
      })}
    >
      <Reveal>
        <SectionHeading
          icon={<InfoIcon />}
          eyebrow={aboutSection.eyebrow}
          title={aboutSection.title}
          subtitle={aboutSection.subtitle}
        />
      </Reveal>

      <Box
        id="about-preview"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        <Reveal style={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={(theme) => ({
              height: "100%",
              p: 2.5,
              borderRadius: 2.5,
              textAlign: "left",
              border: theme.tokens.hairline,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.tokens.cardShadow,
            })}
          >
            <Typography
              variant="h3"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
              }}
            >
              <SchoolIcon fontSize="small" aria-hidden />
              {aboutSection.backgroundTitle}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.75 }}
            >
              {backgroundSummary}
            </Typography>
          </Box>
        </Reveal>

        <Box sx={{ ...CARD_GRID, flex: 1.4 }}>
          {skillHighlights.map((skill, index) => (
            <Reveal key={skill.title} delay={index * 0.05}>
              <SkillCard {...skill} />
            </Reveal>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <ActionButton
          component={RouterLink}
          to="/about"
          endIcon={<ArrowForwardIcon />}
        >
          {aboutSection.cta}
        </ActionButton>
      </Box>
    </Box>

    <Divider sx={{ width: "50%", mx: "auto" }} />

    {/* Projects preview */}
    <Box
      component="section"
      aria-labelledby="projects-preview"
      sx={{ py: { xs: 5, md: 8 } }}
    >
      <Reveal>
        <SectionHeading
          icon={<AccountTreeIcon />}
          eyebrow={projectsSection.eyebrow}
          title={projectsSection.title}
          subtitle={projectsSection.subtitle}
        />
      </Reveal>

      <Box id="projects-preview" sx={CARD_GRID}>
        {featuredProjects.map((project, index) => (
          <Reveal key={project.slug} delay={index * 0.05}>
            <ProjectCard project={project} />
          </Reveal>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <ActionButton
          component={RouterLink}
          to="/projects"
          endIcon={<ArrowForwardIcon />}
        >
          {projectsSection.cta}
        </ActionButton>
      </Box>
    </Box>
  </Container>
);

export default HomePage;
